/* ============================================================================
   Slide 10a — "Bhramari" (All/Tutorial mode) — SHARED CONTENT
   Read by both the desktop layout (HTLDSlide10aInner) and the mobile layout
   (HTLDSlide10aMobile). Edit copy ONLY here — English and German side by side;
   the German object must have exactly the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

// Colour — every Bhramari tint lives here, so a palette change is one edit.
export const accent10a = "#d6c28e";          // champagne
export const accent10aRgb = "214,194,142";   // same colour, for rgba()
export const tint10a = {
  bold:        "#efe3c2",   // highlighted words in body text
  step:        "#ece0bf",   // step titles
  calloutText: "#e6dcbf",   // duration & transition text
  calloutBold: "#faf3e1",
  crumbMuted:  "#9a8a5e",   // mobile breadcrumb
  subMuted:    "#a59c84",   // mobile subtitle
  glowRgb:     "250,242,220", // orb highlight & moon
};

export interface Seg { t: string; b?: boolean; em?: boolean; }

// Practice parameters — shared by the copy and the breathing guide.
export const BHRAMARI_INHALE = 4;
export const BHRAMARI_HUM = 10; // inhale 4 : hum 10 — bhramari-practice.mp3 must match (one round)
export const BHRAMARI_ROUNDS = 5;
/** Where "Exhale" begins in bhramari-practice.mp3 (seconds), per recording
 *  language — measured from the recording; the guide switches to HUM there.
 *  null = no measurement yet: split the clip by counts (inhale : hum). */
export const BHRAMARI_EXHALE_AT: ByLang<number | null> = { en: 6.25, de: null }; // en: 22.5 s clip (2026-10-26)

const en = {
  meta: {
    breadcrumbPre: "Breath Practice",
    breadcrumbAccent: "The humming bee breath",
    sanskrit: "भ्रामरी प्राणायाम",
    headerTitlePre: "Bhramari ",
    headerTitleAccent: "Pranayama",
    titlePre: "Humming Bee ",
    titleAccentWord: "Breath",
    titlePost: "",
    subtitle: "Bhramari · Turning the senses inward — from sound into silence",
    whatLabel: "What it does",
    howLabel: `How to practise · ${BHRAMARI_INHALE} · hum ${BHRAMARI_HUM}`,
    durationLabel: "Duration & transition",
    guideSubtitle: `Humming bee · ${BHRAMARI_INHALE} · hum ${BHRAMARI_HUM} — ${BHRAMARI_ROUNDS} rounds`,
    ariaStart: "Start Bhramari practice",
    ariaStop: "Stop Bhramari practice",
    ariaGuide: `Breathing guide: inhale ${BHRAMARI_INHALE} counts, hum on the exhale for ${BHRAMARI_HUM} counts, ${BHRAMARI_ROUNDS} rounds.`,
  },
  steps: [
    { step: "Close the ears and eyes",        desc: "Thumbs gently press the small flap at the front of each ear, closing the ear opening. The four fingers rest lightly on the closed eyelids, with no pressure. Lips softly closed." },
    { step: "Inhale through the nose — 4",    desc: "Slow, easy breath into the belly." },
    { step: `Hum on the exhale — ${BHRAMARI_HUM} or more`,  desc: "A steady, low “mmm”. Let the sound fill the head. No strain." },
    { step: "Repeat for 5 rounds",            desc: "Keep the pitch comfortable and the volume soft." },
  ] as { step: string; desc: string }[],
  what: [
    { t: "Bhramari — named after " },
    { t: "bhramara", em: true },
    { t: ", the black bee — pairs a long, humming exhale with the ears and eyes gently closed. The vibration draws attention inward and holds it on one gentle sound: " },
    { t: "pratyahara", b: true },
    { t: ", the withdrawal of the senses. The extended exhale slows the breath, and when the humming stops, the " },
    { t: "silence that remains", b: true },
    { t: " is the doorway into meditation." },
  ] as Seg[],
  duration: [
    { t: "Practise " },
    { t: "5 rounds", b: true },
    { t: " after Chandra Bhedana. When the last hum fades, move directly into Meditation." },
  ] as Seg[],
  /** Text drawn on the breathing-guide canvas. */
  canvas: {
    inhale: "INHALE",
    hum: "HUM",
    barInhale: `I${BHRAMARI_INHALE}`,
    barHum: `HUM ${BHRAMARI_HUM}`,
    round: (r: number, of: number) => `round ${r} · of ${of}`,
    idleFooter: `inhale ${BHRAMARI_INHALE} · hum ${BHRAMARI_HUM} · ${BHRAMARI_ROUNDS} rounds`,
    pressPlay: "press ▶ to begin",
    introFooter: "close the eyes · sit or lie comfortably",
  },
};

export type Slide10aContent = typeof en;

const de: Slide10aContent = {
  meta: {
    breadcrumbPre: "Atemübung",
    breadcrumbAccent: "Der summende Bienenatem",
    sanskrit: "भ्रामरी प्राणायाम",
    headerTitlePre: "Bhramari ",
    headerTitleAccent: "Pranayama",
    titlePre: "Summender ",
    titleAccentWord: "Bienenatem",
    titlePost: "",
    subtitle: "Bhramari · Die Sinne nach innen wenden – vom Klang in die Stille",
    whatLabel: "Wirkung",
    howLabel: `So übst du · ${BHRAMARI_INHALE} · summen ${BHRAMARI_HUM}`,
    durationLabel: "Dauer & Übergang",
    guideSubtitle: `Bienenatem · ${BHRAMARI_INHALE} · summen ${BHRAMARI_HUM} – ${BHRAMARI_ROUNDS} Runden`,
    ariaStart: "Bhramari-Übung starten",
    ariaStop: "Bhramari-Übung beenden",
    ariaGuide: `Atemführung: ${BHRAMARI_INHALE} Zählzeiten einatmen, beim Ausatmen ${BHRAMARI_HUM} Zählzeiten summen, ${BHRAMARI_ROUNDS} Runden.`,
  },
  steps: [
    { step: "Ohren und Augen schließen",          desc: "Die Daumen drücken sanft die kleine Klappe vorn an jedem Ohr und verschließen so die Ohröffnung. Die vier Finger ruhen locker auf den geschlossenen Lidern, ohne Druck. Die Lippen sind sanft geschlossen." },
    { step: "Durch die Nase einatmen – 4",        desc: "Langsam und mühelos in den Bauch atmen." },
    { step: `Beim Ausatmen summen – ${BHRAMARI_HUM} oder mehr`, desc: "Ein gleichmäßiges, tiefes „mmm“. Lass den Klang den Kopf füllen. Ohne Anstrengung." },
    { step: "5 Runden wiederholen",               desc: "Halte die Tonhöhe angenehm und die Lautstärke leise." },
  ],
  what: [
    { t: "Bhramari – benannt nach " },
    { t: "bhramara", em: true },
    { t: ", der schwarzen Biene – verbindet ein langes, summendes Ausatmen mit sanft geschlossenen Ohren und Augen. Die Vibration lenkt die Aufmerksamkeit nach innen und hält sie bei einem sanften Klang: " },
    { t: "Pratyahara", b: true },
    { t: ", das Zurückziehen der Sinne. Das lange Ausatmen verlangsamt den Atem, und wenn das Summen endet, ist die " },
    { t: "Stille, die bleibt", b: true },
    { t: ", das Tor zur Meditation." },
  ],
  duration: [
    { t: "Übe " },
    { t: "5 Runden", b: true },
    { t: " nach Chandra Bhedana. Wenn das letzte Summen verklingt, geh direkt in die Meditation über." },
  ],
  canvas: {
    inhale: "EINATMEN",
    hum: "SUMMEN",
    barInhale: `E${BHRAMARI_INHALE}`,
    barHum: `SUMMEN ${BHRAMARI_HUM}`,
    round: (r: number, of: number) => `Runde ${r} · von ${of}`,
    idleFooter: `einatmen ${BHRAMARI_INHALE} · summen ${BHRAMARI_HUM} · ${BHRAMARI_ROUNDS} Runden`,
    pressPlay: "▶ drücken zum Starten",
    introFooter: "Augen schließen · bequem sitzen oder liegen",
  },
};

export const slide10aContent: ByLang<Slide10aContent> = { en, de };
export const useSlide10a = () => useL(slide10aContent);
